import { Component, OnInit, ViewChild } from '@angular/core'
import { FormBuilder } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import { asyncScheduler } from 'rxjs'
import { observeOn } from 'rxjs/operators'
import * as R from 'ramda'

import * as fromRoot from '@tv3/store/state'
import { MessageService } from '@tv3/store/message/message.service'
import { selectUserPreferences } from '@tv3/store/user-preferences/user-preferences.selectors'
import { UpdateUserPreferences } from '@tv3/store/user-preferences/user-preferences.actions'
import { selectAllRentals, Toaster, untilDestroy } from '@tokeet/tv3-platform'
import {
  DeleteMessage,
  LoadLatestMessages,
  MarkOneMessageAsRead,
  RemoveMessageFromFeed,
} from '@tv3/store/message/message.actions'
import { selectMessagesLoaded, selectPinnedMessageViews } from '@tv3/store/message/message.selectors'
import { MessageView } from '@tv3/store/message/message.view'
import { BookingStatus, MessageFilters, MessageReadStatus } from '@tv3/store/message/message.filters'
import { TableType } from '@tv3/shared/empty-table/table-type'
import { inquiriesRouteMapping } from '@tv3/constants/inquiries-route-mapping'
import { OpenGuestOverlay, OpenInquiryOverlay, OpenRentalOverlay } from '@tv3/store/overlay/overlay.actions'
import { UserStorage } from '@tv3/services/utils/storage.service'
import { MatPaginator } from '@angular/material/paginator'
import { ManagedTable } from '@tokeet-frontend/utility'
import { MessageType } from '@tv3/store/message/message.model'

@Component({
  selector: 'app-message-feed',
  templateUrl: './message-feed.component.html',
  styleUrls: ['./message-feed.component.scss'],
})
export class MessageFeedComponent extends ManagedTable implements OnInit {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator

  inboxName = inquiriesRouteMapping.messages.name

  isLoaded = false

  BookingStatus = BookingStatus
  MessageReadStatus = MessageReadStatus
  pageSize: number
  page: {
    pageIndex: number
    pageSize: number
  }
  messages: MessageView[] = []
  pinnedMessages: MessageView[] = []
  data: MessageView[] = []
  userPreferencesId
  rentals$ = this.store.pipe(observeOn(asyncScheduler), select(selectAllRentals))
  filters = this.messageFilters.group
  tableType = TableType.MessageFeed
  inputTooltip = ''
  unreadMessageCount = 0

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private store: Store<fromRoot.State>,
    private toast: Toaster,
    protected storage: UserStorage,
    private messageFilters: MessageFilters
  ) {
    super(storage, TableType.MessageFeed)

    this.pageSize = this.savedPageSize
    this.page = {
      pageIndex: 0,
      pageSize: this.savedPageSize,
    }
  }

  ngOnInit() {
    this.store.dispatch(LoadLatestMessages({ messageType: MessageType.Inbound }))

    this.filters.changes.pipe(untilDestroy(this)).subscribe((messages) => {
      this.messages = messages
      this.unreadMessageCount = R.filter((m) => !m.read, messages || []).length
      this.onSetPage(this.page)
    })

    this.filters.currentFilters.pipe(untilDestroy(this)).subscribe(() => {
      this.paginator.firstPage()
      this.page.pageIndex = 0
    })

    this.store
      .select(selectMessagesLoaded)
      .pipe(untilDestroy(this))
      .subscribe((isLoaded) => (this.isLoaded = isLoaded))

    this.store
      .select(selectPinnedMessageViews)
      .pipe(untilDestroy(this))
      .subscribe((messages) => {
        this.pinnedMessages = messages
      })

    this.store
      .select(selectUserPreferences)
      .pipe(untilDestroy(this))
      .subscribe((userPreferences) => {
        this.userPreferencesId = userPreferences.id
      })
  }

  getGuestName(message) {
    return message.guestName || message.name || 'Unknown'
  }

  onRentalClick(message: MessageView) {
    this.store.dispatch(OpenRentalOverlay({ rental: message.rental }))
  }

  onGuestClick(message) {
    if (!message.guestId) {
      return
    }
    this.store.dispatch(OpenGuestOverlay({ guestId: message.guestId }))
  }

  onClick(message) {
    this.store.dispatch(OpenInquiryOverlay({ inquiryId: message.inquiryId }))
    // mark it as read
    if (!message.read) {
      this.store.dispatch(MarkOneMessageAsRead({ messageId: message.id }))
    }
  }

  onSetPage(page) {
    this.data = this.messages.slice(page.pageIndex * page.pageSize, (page.pageIndex + 1) * page.pageSize)
    this.page = page
    this.onPageChange(page)
  }

  onTogglePinned(message) {
    const pinnedMessageIds = this.pinnedMessages.map((m) => m.id)
    const pinned = message.pinned
      ? pinnedMessageIds.filter((id) => id !== message.id)
      : [...pinnedMessageIds, message.id]
    this.store.dispatch(
      UpdateUserPreferences({
        id: this.userPreferencesId,
        form: {
          message_pins: pinned,
        },
        successMessage: message.pinned ? 'Message unpinned successfully' : 'Message pinned successfully',
      })
    )
  }

  onClearSearch() {
    this.filters.patchValue({
      search: '',
    })
  }

  onRemoveFromFeed(message) {
    this.store.dispatch(RemoveMessageFromFeed({ message }))
  }

  onRemove(message) {
    this.store.dispatch(DeleteMessage({ message, inquiryId: message.inquiryId }))
  }

  clearFilters() {
    this.filters.patchValue(this.messageFilters.defaultValues)
  }
}
